"use client";

import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  // ShoppingCartIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  HomeIcon,
  // MegaphoneIcon,
  CalendarDaysIcon,
  XMarkIcon,
  TagIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { classNames } from "@/utils";
import { Link, Outlet, useNavigate } from "react-location";
import {
  ADMIN_DELIVERY_FEES,
  ADMIN_COUPONS,
  ADMIN_HOME,
  ADMIN_LOCATIONS,
  ADMIN_PAYMENTS,
  ADMIN_PAYOUTS,
  ADMIN_REFUNDS,
  ADMIN_SERVICE_FEES,
  ADMIN_VIDEO_ADS,
  LOGIN,
  SETTINGS,
} from "@/constants";
import { useAppDispatch, useAppSelector } from "@/redux";
import { RootState } from "@/app/store";
import { logout } from "@/redux/features/auth/authSlice";

const baseNavigation = [
  { name: "Dashboard", href: ADMIN_HOME, icon: HomeIcon, current: false },
  {
    name: "Products",
    href: "/products",
    icon: ShoppingBagIcon,
    current: false,
  },

  {
    name: "Services",
    href: "/admin-services",
    icon: UserGroupIcon,
    current: false,
  },
  {
    name: "Orders",
    href: "/admin-orders",
    icon: ShoppingBagIcon,
    current: false,
  },
  {
    name: "Bookings",
    href: "/bookings",
    icon: CalendarDaysIcon,
    current: false,
  },
  {
    name: "Coupons",
    href: ADMIN_COUPONS,
    icon: TagIcon,
    current: false,
  },

  // {
  //   name: "Carts",
  //   href: "/admin-carts",
  //   icon: ShoppingCartIcon,
  //   current: false,
  // },
  // { name: "Ads", href: "/admin-ads", icon: MegaphoneIcon, current: false },
];

export default function AdminLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const userType = user?.user_type;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build navigation, adding Subscriptions only for admins
  const navigation = [...baseNavigation];

  if (userType === "ADMIN" || user?.is_staff || user?.is_superuser) {
    navigation.push(
      {
        name: "Subscription Packages",
        href: "/admin-plans",
        icon: TagIcon,
        current: false,
      },
      {
        name: "Subscription",
        href: "/admin-subscriptions",
        icon: TagIcon,
        current: false,
      },
      {
        name: "Banners",
        href: "/admin-banners",
        icon: PhotoIcon,
        current: false,
      },
      { name: "Users", href: "/users", icon: UserGroupIcon, current: false },
      {
        name: "Vendors",
        href: "/vendors",
        icon: UserGroupIcon,
        current: false,
      },
      {
        name: "Locations",
        href: ADMIN_LOCATIONS,
        icon: TagIcon,
        current: false,
      },
      {
        name: "Delivery Fees",
        href: ADMIN_DELIVERY_FEES,
        icon: TagIcon,
        current: false,
      },
      {
        name: "Service Fees",
        href: ADMIN_SERVICE_FEES,
        icon: TagIcon,
        current: false,
      },
      {
        name: "Video Ads",
        href: ADMIN_VIDEO_ADS,
        icon: PhotoIcon,
        current: false,
      },
      {
        name: "Payouts",
        href: ADMIN_PAYOUTS,
        icon: TagIcon,
        current: false,
      },
      {
        name: "Payments",
        href: ADMIN_PAYMENTS,
        icon: TagIcon,
        current: false,
      },
      {
        name: "Refunds",
        href: ADMIN_REFUNDS,
        icon: TagIcon,
        current: false,
      },
      { name: "Category", href: "/categories", icon: TagIcon, current: false }
    );
  }

  const handleLogout = () => {
    dispatch(logout());
    navigate({ to: LOGIN });
  };

  const userNavigation = [
    {
      name: "Your profile",

      onClick: () => {
        navigate({ to: "/profile" });
      },
    },
    { name: "Sign out", onClick: handleLogout },
  ];

  const soonItems = navigation.filter(
    (item) => item.name === "Carts" || item.name === "Ads"
  );

  const mainItems = navigation.filter(
    (item) => item.name !== "Carts" && item.name !== "Ads"
  );

  const orderedNav = [...mainItems, ...soonItems];

  return (
    <>
      <div className="font-inter">
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                <div
                  className="flex h-16 shrink-0 items-center"
                  onClick={() => navigate({ to: ADMIN_HOME })}
                >
                  <img
                    alt="Your Company"
                    src="/logo.png"
                    className="h-8 w-auto cursor-pointer"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {orderedNav.map((item) => {
                          const isSoon =
                            (item as (typeof navigation)[number]).name ===
                              "Carts" ||
                            (item as (typeof navigation)[number]).name ===
                              "Ads";
                          return (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                className={classNames(
                                  item.current
                                    ? "bg-gray-800 text-white"
                                    : "text-gray-400 hover:bg-gray-800 hover:text-white",
                                  "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                                )}
                              >
                                <item.icon
                                  aria-hidden="true"
                                  className="size-6 shrink-0"
                                />
                                <span>{item.name}</span>
                                {isSoon && (
                                  <span className="inline-block rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-gray-900">
                                    Soon
                                  </span>
                                )}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </li>

                    <li className="mt-auto">
                      <Link
                        to={SETTINGS}
                        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-400 hover:bg-gray-800 hover:text-white"
                      >
                        <Cog6ToothIcon
                          aria-hidden="true"
                          className="size-6 shrink-0"
                        />
                        Settings
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
            <div
              className="flex h-16 shrink-0 items-center"
              onClick={() => navigate({ to: ADMIN_HOME })}
            >
              <img
                alt="Your Company"
                src="/logo.png"
                className="h-8 w-auto object-contain rounded-md cursor-pointer"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-2">
                    {orderedNav.map((item) => {
                      const isSoon =
                        item.name === "Carts" || item.name === "Ads";
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={classNames(
                              item.current
                                ? "bg-gray-800 text-white"
                                : "text-gray-400 hover:bg-gray-800 hover:text-white",
                              "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                            )}
                          >
                            <item.icon
                              aria-hidden="true"
                              className="size-6 shrink-0"
                            />
                            <span>{item.name}</span>
                            {isSoon && (
                              <span className=" inline-block rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-gray-900">
                                Soon
                              </span>
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>

                <li className="mt-auto">
                  <Link
                    to={SETTINGS}
                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    <Cog6ToothIcon
                      aria-hidden="true"
                      className="size-6 shrink-0"
                    />
                    Settings
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            {/* Separator */}
            <div
              aria-hidden="true"
              className="h-6 w-px bg-gray-900/10 lg:hidden"
            />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form action="#" method="GET" className="grid flex-1 grid-cols-1">
                <input
                  name="search"
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  className="col-start-1 row-start-1 block size-full bg-white pl-8 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm/6"
                />
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
                />
              </form>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <button
                  type="button"
                  className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="size-6" />
                </button>

                {/* Separator */}
                <div
                  aria-hidden="true"
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <img
                      alt=""
                      src={
                        user?.avatar ||
                        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      }
                      className="size-8 rounded-full bg-gray-50"
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        aria-hidden="true"
                        className="ml-4 text-sm/6 font-semibold text-gray-900"
                      >
                        {user?.name || "User Name"}
                      </span>
                      <ChevronDownIcon
                        aria-hidden="true"
                        className="ml-2 size-5 text-gray-400"
                      />
                    </span>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 ring-1 shadow-lg ring-gray-900/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                  >
                    {userNavigation.map((item) => (
                      <MenuItem key={item.name}>
                        <button
                          onClick={item.onClick}
                          className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                        >
                          {item.name}
                        </button>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          <main className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
